import React, { useState, useEffect, useRef } from 'react';
import { Menu, Bell, Sun, Moon, Search, ChevronDown, LogOut, CheckCheck, Package, FlaskConical, Calendar, Headphones, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../hooks';
import { useAuth } from '../context/AuthContext';
import { notificationService } from '../services/api';
import { formatDate } from '../utils/helpers';

/* ── Notification type icon ──────────────────────────────────────────────── */
const NOTIF_ICONS = {
  order_placed:    { icon: Package,      bg: 'bg-blue-100 dark:bg-blue-900/30',    color: 'text-blue-600 dark:text-blue-400' },
  lab_booking:     { icon: FlaskConical, bg: 'bg-purple-100 dark:bg-purple-900/30', color: 'text-purple-600 dark:text-purple-400' },
  appointment:     { icon: Calendar,     bg: 'bg-green-100 dark:bg-green-900/30',   color: 'text-green-600 dark:text-green-400' },
  support_ticket:  { icon: Headphones,   bg: 'bg-orange-100 dark:bg-orange-900/30', color: 'text-orange-600 dark:text-orange-400' },
  default:         { icon: AlertCircle,  bg: 'bg-gray-100 dark:bg-gray-700',        color: 'text-gray-500 dark:text-gray-400' },
};

const getNotifMeta = (type) => NOTIF_ICONS[type] || NOTIF_ICONS.default;

const Navbar = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [dark, toggleDark] = useDarkMode();
  const [showNotif, setShowNotif]   = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [pagination, setPagination]       = useState({});
  const [page, setPage]                   = useState(1);
  const [loading, setLoading]             = useState(false);
  const pollRef = useRef(null);

  const fetchNotifications = async (p = 1) => {
    try {
      setLoading(true);
      const res = await notificationService.getAll(p);
      setNotifications(p === 1 ? res.notifications : prev => [...prev, ...res.notifications]);
      setUnreadCount(res.unread_count);
      setPagination(res.pagination);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  // Initial fetch + poll every 60s
  useEffect(() => {
    fetchNotifications(1);
    pollRef.current = setInterval(() => fetchNotifications(1), 60000);
    return () => clearInterval(pollRef.current);
  }, []);

  const handleMarkRead = async (notif) => {
    if (notif.is_read) return;
    try {
      await notificationService.markRead(notif.id);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: 1 } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch { /* silent */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchNotifications(nextPage);
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <header className="h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center px-4 gap-4 sticky top-0 z-10">
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Search */}
      <div className="hidden md:flex relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          className="input-field pl-9 py-1.5 text-sm"
          placeholder="Search anything..."
        />
      </div>

      <div className="flex items-center gap-2 ml-auto">
        {/* Dark mode */}
        <button
          onClick={toggleDark}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
        >
          {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => {
              setShowNotif((s) => !s);
              setShowProfile(false);
            }}
            className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          {showNotif && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowNotif(false)}
              />
              <div className="absolute right-0 sm:right-0 left-1/2 sm:left-auto -translate-x-1/2 sm:translate-x-0 top-full mt-2 w-[85vw] sm:w-96 max-w-[380px] card shadow-xl z-20 overflow-hidden flex flex-col max-h-[80vh]">
                {/* Header */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-gray-900 dark:text-white">
                      Notifications
                    </span>
                    {unreadCount > 0 && (
                      <span className="px-1.5 py-0.5 text-xs font-bold bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-full">
                        {unreadCount} new
                      </span>
                    )}
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="flex items-center gap-1 text-xs text-medihub-primary dark:text-medihub-highlight hover:underline"
                    >
                      <CheckCheck className="w-3.5 h-3.5" />
                      Mark all read
                    </button>
                  )}
                </div>

                {/* List */}
                <div className="overflow-y-auto flex-1">
                  {loading && notifications.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-400">
                      Loading…
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="p-8 text-center">
                      <Bell className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">
                        No notifications yet
                      </p>
                    </div>
                  ) : (
                    notifications.map((n) => {
                      const meta = getNotifMeta(n.type);
                      const Icon = meta.icon;
                      return (
                        <div
                          key={n.id}
                          onClick={() => handleMarkRead(n)}
                          className={`flex items-start gap-3 p-3 border-b border-gray-100 dark:border-gray-700/50 last:border-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors ${
                            !n.is_read
                              ? "bg-blue-50/50 dark:bg-blue-900/10"
                              : ""
                          }`}
                        >
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${meta.bg}`}
                          >
                            <Icon className={`w-4 h-4 ${meta.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-sm leading-snug ${!n.is_read ? "font-semibold text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-300"}`}
                            >
                              {n.title}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                              {n.message}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                              {formatDate(n.created_at)}
                            </p>
                          </div>
                          {!n.is_read && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0 mt-2" />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Load more */}
                {pagination.has_next && (
                  <div className="p-2 border-t border-gray-200 dark:border-gray-700 shrink-0">
                    <button
                      onClick={handleLoadMore}
                      disabled={loading}
                      className="w-full text-xs text-center text-medihub-primary dark:text-medihub-highlight hover:underline py-1 disabled:opacity-50"
                    >
                      {loading ? "Loading…" : "Load more"}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Profile */}
        <div className="relative">
          <button
            onClick={() => {
              setShowProfile((s) => !s);
              setShowNotif(false);
            }}
            className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="w-8 h-8 bg-medihub-primary rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {user?.name?.[0] || "A"}
            </div>
            <span className="hidden sm:block text-sm font-medium text-gray-700 dark:text-gray-300">
              {user?.name || "Admin"}
            </span>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>

          {showProfile && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowProfile(false)}
              />
              <div className="absolute right-0 top-full mt-2 w-56 card shadow-lg z-20 overflow-hidden">
                <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {user?.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {user?.email}
                  </p>
                </div>
                <div className="p-1">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
